import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LifeBuoy, AlertCircle, Radio, User, MapPin, Clock,
  Send, Loader2, ChevronLeft, RefreshCw,
} from 'lucide-react';
import api from '../../utils/api';

export default function LiveEventInbox() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');
  const [replying, setReplying] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/live-event-inbox');
      setThreads(r.data.threads || []);
    } catch (e) {
      toast.error('Failed to load onsite conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (threadId) => {
    if (!threadId) return;
    try {
      const r = await api.get(`/admin/live-event-inbox/${threadId}`);
      setDetail(r.data);
    } catch (e) {
      toast.error('Failed to load conversation');
    }
  }, []);

  useEffect(() => {
    load();
    // Poll every 15s while page is open — cheap real-time.
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Deep-link support: /admin/live-event-inbox?thread=<id>
  useEffect(() => {
    const t = params.get('thread');
    if (t && t !== selectedId) {
      setSelectedId(t);
      loadDetail(t);
    }
  }, [params, selectedId, loadDetail]);

  const handleSelect = (id) => {
    setSelectedId(id);
    setDetail(null);
    setReply('');
    setParams({ thread: id }, { replace: true });
    loadDetail(id);
  };

  const handleReply = async () => {
    if (!reply.trim() || !selectedId) return;
    setReplying(true);
    try {
      await api.post(`/admin/live-event-inbox/${selectedId}/reply`, { content: reply.trim() });
      setReply('');
      toast.success('Reply sent (in-app + SMS to talent)');
      await loadDetail(selectedId);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reply');
    } finally {
      setReplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Radio className="w-6 h-6 text-orange-600" />
              Live Event Inbox
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Onsite talent messages. Currently-clocked-in talent surface first.
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
            data-testid="lei-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Thread list */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 max-h-[80vh] overflow-y-auto" data-testid="lei-thread-list">
            {loading && threads.length === 0 && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
              </div>
            )}
            {!loading && threads.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                <LifeBuoy className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                No onsite messages yet. When a talent uses "Message iEvntür"
                from an active shift, their conversation will land here.
              </div>
            )}
            {threads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => handleSelect(t.thread_id)}
                className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 ${
                  selectedId === t.thread_id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
                } ${t.talent_is_clocked_in ? 'bg-orange-50/40' : ''}`}
                data-testid={`lei-thread-${t.thread_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {t.talent_is_clocked_in && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                          <Radio className="w-2.5 h-2.5" /> Onsite
                        </span>
                      )}
                      {t.priority === 'onsite_urgent' && !t.talent_is_clocked_in && (
                        <span className="text-[10px] font-bold uppercase text-amber-700">Urgent</span>
                      )}
                      {t.unread_count > 0 && (
                        <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-600 text-white text-[10px] font-bold">
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-sm text-slate-900 truncate mt-0.5">
                      {t.talent_name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {t.venue_name || t.event_title || '—'} · {t.shift_date}
                    </div>
                    <div className="text-xs text-slate-700 truncate mt-1">
                      {t.last_message}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 whitespace-nowrap">
                    {t.last_message_at
                      ? new Date(t.last_message_at).toLocaleTimeString([],{ hour: '2-digit', minute: '2-digit' })
                      : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Conversation pane */}
          <div className="md:col-span-2 bg-white rounded-lg shadow-sm border border-slate-200 flex flex-col max-h-[80vh]">
            {!selectedId && (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-8">
                Select a conversation to reply.
              </div>
            )}
            {selectedId && !detail && (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
              </div>
            )}
            {selectedId && detail && (
              <>
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60">
                  <button
                    onClick={() => { setSelectedId(null); setDetail(null); setParams({}, { replace: true }); }}
                    className="md:hidden inline-flex items-center gap-1 text-slate-600 text-xs mb-1"
                  >
                    <ChevronLeft className="w-3 h-3" /> Back
                  </button>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="inline-flex items-center gap-1 font-bold text-slate-900">
                      <User className="w-3.5 h-3.5" /> {detail.thread.talent_name}
                    </span>
                    <a href={`tel:${detail.thread.talent_phone}`} className="text-xs text-cyan-700 hover:underline" data-testid="lei-talent-phone-link">
                      {detail.thread.talent_phone}
                    </a>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <MapPin className="w-3.5 h-3.5" /> {detail.thread.venue_name || detail.thread.event_title || '—'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <Clock className="w-3.5 h-3.5" /> {detail.thread.shift_date}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="lei-messages">
                  {(detail.messages || []).map((m) => (
                    <div
                      key={m.id}
                      className={`flex ${m.sender_role === 'talent' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                        m.sender_role === 'talent'
                          ? 'bg-slate-100 text-slate-900 rounded-bl-sm'
                          : 'bg-orange-600 text-white rounded-br-sm'
                      }`}>
                        <div>{m.content}</div>
                        <div className={`text-[10px] mt-1 ${m.sender_role === 'talent' ? 'text-slate-500' : 'text-orange-100'}`}>
                          {m.sender_role === 'talent' ? detail.thread.talent_name.split(' ')[0] : 'You'}
                          {m.created_at ? ` · ${new Date(m.created_at).toLocaleTimeString([],{ hour: '2-digit', minute: '2-digit' })}` : ''}
                          {m.delivery_status === 'failed' && ' · failed'}
                          {m.channel_hint === 'sms' && ' · sent via SMS'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* System-failure surface if any recent send attempts failed */}
                {(detail.send_attempts || []).some((a) =>
                  ['failed', 'client_error', 'provider_error'].includes(a.status),
                ) && (
                  <div className="mx-4 mb-2 px-3 py-2 rounded border border-rose-300 bg-rose-50 text-rose-900 text-xs flex gap-2" data-testid="lei-send-failures">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <strong>System messaging failures detected.</strong> {' '}
                      {(detail.send_attempts || []).filter((a) => ['failed', 'client_error', 'provider_error'].includes(a.status)).length} attempt(s) did not deliver. This is a platform issue — the talent should NOT be marked non-compliant.
                    </div>
                  </div>
                )}

                <div className="p-3 border-t border-slate-200 bg-white">
                  <div className="flex gap-2">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleReply();
                      }}
                      placeholder="Reply to talent (⌘/Ctrl+Enter to send). Reply goes in-app + SMS."
                      className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                      rows={2}
                      data-testid="lei-reply-textarea"
                    />
                    <button
                      onClick={handleReply}
                      disabled={!reply.trim() || replying}
                      className="self-end inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold"
                      data-testid="lei-reply-send-btn"
                    >
                      {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
